import React, { useState, useCallback, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/DatePicker.css';
import { marked } from 'marked';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    Autocomplete,
    Chip,
    useMediaQuery,
    useTheme,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Build as BuildIcon,
    Save as SaveIcon,
    Folder as FolderIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    AttachMoney as AttachMoneyIcon,
    Comment as CommentIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectTransformers } from '../../../store/selectors/supervisionSelector';
import { $authHost } from '../../../http';
import { ktpIcon, projectStatusOreolIcon } from '../../layers/icons';

// Регистрируем русскую локаль
registerLocale('ru', ru);

// Компонент для отображения markdown контента
const MarkdownContent = ({ content, darkMode }) => {
    if (!content) return null;

    const htmlContent = marked(content);

    return (
        <Box
            sx={{
                '& p': {
                    margin: '1px 0',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                },
                '& strong': {
                    fontWeight: 'bold',
                },
                '& em': {
                    fontStyle: 'italic',
                },
                '& ul, & ol': {
                    margin: '1px 0',
                    paddingLeft: '8px',
                },
                '& li': {
                    margin: '0px 0',
                    fontSize: '0.875rem',
                },
                '& code': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '1px 2px',
                    borderRadius: '2px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                },
                '& pre': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '2px',
                    borderRadius: '2px',
                    overflow: 'auto',
                    fontSize: '0.8rem',
                },
                '& blockquote': {
                    borderLeft: `2px solid ${darkMode ? '#2196f3' : '#1565c0'}`,
                    paddingLeft: '4px',
                    margin: '1px 0',
                    fontStyle: 'italic',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '1px 0',
                    fontWeight: 'bold',
                },
                '& h1': { fontSize: '1.1rem' },
                '& h2': { fontSize: '1rem' },
                '& h3': { fontSize: '0.95rem' },
                '& h4': { fontSize: '0.9rem' },
                '& h5': { fontSize: '0.85rem' },
                '& h6': { fontSize: '0.8rem' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
};

// Тестовые данные для autocomplete
const mockObjectNames = [
    'Объект 1 - Строительство ВЛ 10кВ',
    'Объект 2 - Реконструкция КТП',
    'Объект 3 - Монтаж оборудования',
    'Объект 4 - Прокладка кабеля',
    'Объект 5 - Установка опор',
];



const mockEquipment = [
    'КТП-1 10/0,4кВ',
    'КТП-2 10/0,4кВ',
    'ВЛ-10кВ',
    'Кабель СИП-3',
    'Опора железобетонная',
    'Трансформатор ТМ-160',
    'Выключатель ВВ/TEL',
    'Разъединитель РЛН',
];

const objectTypes = {
    'transformers': 'ТП 6(10)/0,4 кВ'
}



const ObjectForm = ({
    formData,
    darkMode,
    isEditing,
    setFormData,
    objects = [],
    stages = [],
    responsible = [],
    equipment = [],
    projects = [],
    selectedProjectId = null,
    sortedProjects = [],
    blockedProjects = [],
    isCheckingProjects = false
}) => {
    console.log(formData)
    console.log(responsible, equipment)

    const fieldColors = {
        name: { light: '#2e7d32', dark: '#4caf50' },
        project: { light: '#9c27b0', dark: '#ba68c8' },
        dates: { light: '#1976d2', dark: '#64b5f6' },
        stage: { light: '#6a1b9a', dark: '#ab47bc' },
        responsible: { light: '#d32f2f', dark: '#f44336' },
        coexecutors: { light: '#f57c00', dark: '#ffb74d' },
        equipment: { light: '#3949ab', dark: '#5c6bc0' },
        closing: { light: '#e65100', dark: '#ff8f00' },
        cost: { light: '#c2185b', dark: '#e91e63' },
        comment: { light: '#1565c0', dark: '#2196f3' },
    };

    // Состояния для режимов полей (single/multiple)
    const [fieldModes, setFieldModes] = useState({
        start: 'single',      // Начало строительства
        end: 'single',        // Завершение строительства
        stage: 'single',      // Этап
        responsible: 'single', // Ответственный
        coexecutors: 'single', // Соисполнители
    });

    // Функция переключения режима поля
    const toggleFieldMode = (fieldName) => {
        if (Array.isArray(formData[fieldName])) {
            if (fieldName === 'coexecutors') {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: Array.isArray(prev[fieldName][0]) ? prev[fieldName][0] : [[...prev[fieldName]], ...Array.from({ length: prev.equipment.length - 1 }, () => ([...prev[fieldName]]))]
                }))

            } else {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: prev[fieldName][0]
                }))
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: [prev[fieldName], ...Array.from({ length: prev.equipment.length - 1 }, () => prev[fieldName])]
            }))
        }
    };

    // Получаем количество строк оснащения
    const equipmentRows = formData.equipment?.length > 0 ? formData.equipment : [null];

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            pt: 2,
            gap: 1,
            position: 'relative',
            zIndex: 1,
            '& .MuiTextField-root': {
                '& .MuiOutlinedInput-root': {
                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1,
                    '& input, & textarea': {
                        cursor: 'text !important',
                        pointerEvents: 'auto',
                        zIndex: 2,
                        color: darkMode ? '#ffffff' : '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)'
                    },
                    '&:hover': {
                        bgcolor: darkMode ? '#424242' : '#fafafa',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'
                        }
                    },
                    '&.Mui-focused': {
                        bgcolor: darkMode ? '#424242' : '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                        }
                    },
                    '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? '#f44336' : '#d32f2f'
                        }
                    },
                    // Стили для кнопок и иконок в TextField
                    '& .MuiInputAdornment-root': {
                        color: darkMode ? '#e0e0e0' : '#666'
                    },
                    '& .MuiIconButton-root': {
                        color: darkMode ? '#e0e0e0' : '#666',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                        }
                    }
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    fontWeight: 500,
                    '&.Mui-focused': {
                        fontWeight: 600,
                        color: darkMode ? '#ffffff' : '#1976d2'
                    }
                },
                '& .MuiFormHelperText-root': {
                    color: darkMode ? '#f44336' : '#d32f2f'
                }
            },
            // Стили для Autocomplete кнопок и иконок
            '& .MuiAutocomplete-root': {
                '& .MuiAutocomplete-clearIndicator': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    '&:hover': {
                        color: darkMode ? '#ffffff' : '#333',
                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                    }
                },
                '& .MuiAutocomplete-popupIndicator': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    '&:hover': {
                        color: darkMode ? '#ffffff' : '#333',
                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                    }
                },
                '& .MuiAutocomplete-endAdornment': {
                    color: darkMode ? '#e0e0e0' : '#666'
                }
            }
        }}>
            {/* Наименование объекта */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.name.dark : fieldColors.name.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <BusinessIcon sx={{ fontSize: '16px' }} />
                    Наименование *
                </Typography>
                <Autocomplete
                    fullWidth
                    disabled={isEditing}
                    options={objects}
                    groupBy={(option) => objectTypes[option.type] || 'Неизвестный тип'}
                    getOptionLabel={(option) => `${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`}
                    isOptionEqualToValue={(option, value) => option.fid === value.fid}
                    value={formData.object || null}
                    onChange={(event, newValue) => {
                        setFormData(prev => {
                            return {
                                ...prev,
                                object: newValue || null,
                            };
                        });
                    }}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;

                        const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                        return options.filter(option => {
                            const optionText = `${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`.toLowerCase();

                            return searchTerms.every(term => optionText.includes(term));
                        });
                    }}
                    freeSolo
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id || option.fid} sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            alignItems: 'center',
                            color: darkMode ? '#ffffff' : '#333',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                            }
                        }}>
                            <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                                <Box style={{ position: 'absolute', margin: '5px 0 0 6px' }}>
                                    {ktpIcon('default', 20, 20)}
                                </Box>
                                {['2', '3', '4', '7'].includes(option?.layer) ? (
                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                        {projectStatusOreolIcon('ktp', Number(option?.layer), 32)}
                                    </Box>
                                ) : null}
                            </Box>
                            <Typography sx={{
                                color: darkMode ? '#ffffff' : '#333',
                            }}>
                                {`${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`}
                            </Typography>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={"Выберите или введите наименование объекта"}
                            required
                            error={!formData.object?.name?.trim()}
                            sx={{
                                '& .MuiInputBase-root': {
                                    p: '0px 10px',
                                },
                                '& .MuiOutlinedInput-input': {
                                    p: 1,
                                    cursor: 'text',
                                    pointerEvents: 'auto',
                                    '&::placeholder': {
                                        color: darkMode ? '#999' : '#666',
                                        opacity: 1
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                    bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        color: darkMode ? '#666' : '#999',
                                        cursor: 'not-allowed',
                                        '-webkit-text-fill-color': darkMode ? '#666' : '#999'
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? fieldColors.name.dark : fieldColors.name.light,
                                }
                            }}
                        />
                    )}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                        }
                                    },
                                    '& .MuiAutocomplete-groupLabel': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                        color: darkMode ? '#ffffff' : '#333',
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        padding: '0px 16px',
                                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)'
                                    }
                                }
                            }
                        }
                    }}
                />
            </Box>



            {/* Проект */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.project.dark : fieldColors.project.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <FolderIcon sx={{ fontSize: '16px' }} />
                    Проект
                    {isCheckingProjects && (
                        <Typography variant="caption" sx={{ ml: 1, color: darkMode ? '#999' : '#666' }}>
                            (проверка...)
                        </Typography>
                    )}
                </Typography>
                <Autocomplete
                    fullWidth
                    options={sortedProjects}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={formData.project || null}
                    error={!formData.project}
                    disableClearable
                    onChange={(event, newValue) => {
                        // Проверяем, что выбранный проект не заблокирован
                        const isBlocked = blockedProjects.some(p => p.id === newValue?.id);
                        if (isBlocked) {
                            return; // Не позволяем выбирать заблокированные проекты
                        }
                        setFormData(prev => ({
                            ...prev,
                            project: newValue || null
                        }));
                    }}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;

                        const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                        return options.filter(option => {
                            const optionText = option.name.toLowerCase();

                            return searchTerms.every(term => optionText.includes(term));
                        });
                    }}
                    renderOption={(props, option) => {
                        const isBlocked = blockedProjects.some(p => p.id === option.id);
                        return (
                            <Box component="li" {...props} sx={{
                                color: isBlocked ? (darkMode ? '#666' : '#999') : (darkMode ? '#ffffff' : '#333'),
                                opacity: isBlocked ? 0.6 : 1,
                                cursor: isBlocked ? 'not-allowed' : 'pointer',
                                '&:hover': {
                                    bgcolor: isBlocked ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)')
                                }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <Typography sx={{
                                            color: isBlocked ? (darkMode ? '#666' : '#999') : (darkMode ? '#ffffff' : '#333'),
                                            textDecoration: isBlocked ? 'line-through' : 'none'
                                        }}>
                                            {option.name}
                                        </Typography>
                                        {isBlocked && (
                                            <Typography variant="caption" sx={{
                                                color: darkMode ? '#ff6b6b' : '#f44336',
                                                fontSize: '0.75rem',
                                                fontWeight: 500
                                            }}>
                                                (объект уже существует)
                                            </Typography>
                                        )}
                                    </Box>
                                    {option.description && (
                                        <Typography variant="caption" sx={{
                                            color: isBlocked ? (darkMode ? '#555' : '#777') : (darkMode ? '#cccccc' : 'text.secondary')
                                        }}>
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Выберите проект"
                            sx={{
                                '& .MuiInputBase-root': {
                                    p: '0px 10px',
                                },
                                '& .MuiOutlinedInput-input': {
                                    p: 1,
                                    cursor: 'text',
                                    pointerEvents: 'auto',
                                    '&::placeholder': {
                                        color: darkMode ? '#999' : '#666',
                                        opacity: 1
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? fieldColors.project.dark : fieldColors.project.light,
                                }
                            }}
                        />
                    )}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                        }
                                    }
                                }
                            }
                        }
                    }}
                />
            </Box>

            {/* Оснащение - Выбор */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <BuildIcon sx={{ fontSize: '16px' }} />
                    Оснащение
                </Typography>
                <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    options={equipment}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={formData.equipment || []}
                    onChange={(event, value) => {
                        const changeCount = value.length - formData.equipment.length
                        let relatedFields = {}
                        const fields = ['plan_start', 'plan_end', 'fact_start', 'fact_end', 'stage', 'closing', 'cost', 'comment', 'responsible', 'coexecutors']
                        fields.forEach((field, index) => {
                            if (!Array.isArray(formData[field])) return
                            if (field === 'coexecutors' && formData[field] && !Array.isArray(formData[field][0])) return
                            const newValue = changeCount <= 0 ? formData[field].slice(0, value.length) : [...formData[field], ...Array.from({ length: changeCount }, () => null)]
                            relatedFields[field] = newValue.length === 0 ? [null] : newValue
                        })
                        setFormData(prev => ({
                            ...prev,
                            equipment: value,
                            ...relatedFields
                        }))
                    }}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;

                        const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                        return options.filter(option => {
                            const optionText = option.name.toLowerCase();

                            return searchTerms.every(term => optionText.includes(term));
                        });
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                label={option.name}
                                size="small"
                                sx={{
                                    bgcolor: darkMode ? `${fieldColors.equipment.dark}20` : `${fieldColors.equipment.light}20`,
                                    color: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                                    border: `1px solid ${darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light}50`,
                                }}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{
                                '& .MuiInputBase-root': {
                                    p: '3px 10px !important',
                                },
                                '& .MuiOutlinedInput-input': {
                                    p: 1,
                                    cursor: 'text',
                                    pointerEvents: 'auto',
                                    '&::placeholder': {
                                        color: darkMode ? '#999' : '#666',
                                        opacity: 1
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                                }
                            }}
                        />
                    )}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#ffffff' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                                        }
                                    }
                                }
                            }
                        }
                    }}
                />
            </Box>

            {/* Таблица данных */}
            <Box sx={{ mt: 2, mb: 1 }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 2,
                        maxHeight: '60vh',
                        overflow: 'auto',
                        // Стилизация скроллбара
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: darkMode ? '#1e1e1e' : '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: darkMode ? '#555' : '#c1c1c1',
                            borderRadius: '4px',
                            '&:hover': {
                                background: darkMode ? '#777' : '#a8a8a8',
                            },
                        },
                        '&::-webkit-scrollbar-corner': {
                            background: darkMode ? '#1e1e1e' : '#f1f1f1',
                        },
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-root': {
                                borderBottom: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                borderBottom: darkMode ? '2px solid rgba(255,255,255,0.3)' : '2px solid rgba(0,0,0,0.3)',
                            },
                            '& .MuiTableRow-root:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <BuildIcon sx={{ fontSize: '16px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Оснащение</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" colSpan={2} sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                    fontWeight: 700,
                                    minWidth: 200,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarIcon sx={{ fontSize: '16px' }} />
                                            <Tooltip title={'Плановое начало | Фактическое начало'}>
                                                <Typography variant="subtitle2">Начало строительства</Typography>
                                            </Tooltip>
                                            <Checkbox
                                                size="small"
                                                checked={Array.isArray(formData.plan_start)}
                                                onChange={() => {
                                                    toggleFieldMode('plan_start')
                                                    toggleFieldMode('fact_start')
                                                }}
                                                title="Применить ко всему объекту"
                                                sx={{
                                                    p: 0,
                                                    color: darkMode ? '#60F66D' : '#3DD219',
                                                    '&.Mui-checked': {
                                                        color: darkMode ? '#60F66D' : '#3DD219',
                                                    },

                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" colSpan={2} sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                    fontWeight: 700,
                                    minWidth: 200,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Завершение строительства</Typography>
                                            <Checkbox
                                                size="small"
                                                checked={Array.isArray(formData.plan_end)}
                                                onChange={() => {
                                                    toggleFieldMode('plan_end')
                                                    toggleFieldMode('fact_end')
                                                }}
                                                title="Применить ко всему объекту"
                                                sx={{
                                                    p: 0,
                                                    color: darkMode ? '#60F66D' : '#3DD219',
                                                    '&.Mui-checked': {
                                                        color: darkMode ? '#60F66D' : '#3DD219',
                                                    },

                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.stage.dark : fieldColors.stage.light,
                                    fontWeight: 700,
                                    minWidth: 80,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <BuildIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Этап</Typography>
                                            <Checkbox
                                                size="small"
                                                checked={Array.isArray(formData.stage)}
                                                onChange={() => toggleFieldMode('stage')}
                                                title="Применить ко всему объекту"
                                                sx={{
                                                    p: 0,
                                                    color: darkMode ? '#60F66D' : '#3DD219',
                                                    '&.Mui-checked': {
                                                        color: darkMode ? '#60F66D' : '#3DD219',
                                                    },

                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.closing.dark : fieldColors.closing.light,
                                    fontWeight: 700,
                                    minWidth: 100,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CheckCircleIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Закрытие без НДС</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.cost.dark : fieldColors.cost.light,
                                    fontWeight: 700,
                                    minWidth: 100,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <AttachMoneyIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Стоимость без НДС</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CommentIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Комментарий</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.responsible.dark : fieldColors.responsible.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Ответственный</Typography>
                                            <Checkbox
                                                size="small"
                                                checked={Array.isArray(formData.responsible)}
                                                onChange={() => toggleFieldMode('responsible')}
                                                title="Применить ко всему объекту"
                                                sx={{
                                                    p: 0,
                                                    color: darkMode ? '#60F66D' : '#3DD219',
                                                    '&.Mui-checked': {
                                                        color: darkMode ? '#60F66D' : '#3DD219',
                                                    },

                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.coexecutors.dark : fieldColors.coexecutors.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Соисполнители</Typography>
                                            <Checkbox
                                                size="small"
                                                checked={formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0])}
                                                onChange={() => toggleFieldMode('coexecutors')}
                                                title="Применить ко всему объекту"
                                                sx={{
                                                    p: 0,
                                                    color: darkMode ? '#60F66D' : '#3DD219',
                                                    '&.Mui-checked': {
                                                        color: darkMode ? '#60F66D' : '#3DD219',
                                                    },

                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            {/* <TableRow>
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5' }} />
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                }}>План</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>Факт</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                }}>План</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>Факт</TableCell>
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5' }} />
                            </TableRow> */}
                        </TableHead>
                        <TableBody>
                            {equipmentRows.map((equipItem, rowIndex) => (
                                <TableRow key={rowIndex} sx={{
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                    }
                                }}>
                                    {/* Оснащение */}
                                    <TableCell sx={{
                                        color: darkMode ? '#fff' : '#000',
                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                        p: 0.5,
                                    }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                            {equipItem?.name || '-'}
                                        </Typography>
                                    </TableCell>

                                    {/* Начало строительства - План */}
                                    {(Array.isArray(formData.plan_start) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.plan_start) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                            p: 0.5,
                                        }} >
                                            <DatePicker
                                                selected={Array.isArray(formData.plan_start) ? (formData.plan_start[rowIndex] ? new Date(formData.plan_start[rowIndex]) : null) : (formData.plan_start ? new Date(formData.plan_start) : null)}
                                                onChange={(date) => {
                                                    const formattedDate = date ?
                                                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                        '';

                                                    if (Array.isArray(formData.plan_start)) {
                                                        let newValue = [...formData.plan_start]
                                                        newValue[rowIndex] = formattedDate
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'plan_start': newValue
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'plan_start': formattedDate
                                                        }));
                                                    }
                                                }}
                                                locale="ru"
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="ДД.ММ.ГГГГ"
                                                isClearable
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                yearDropdownItemNumber={10}
                                                scrollableYearDropdown
                                                portalId="root-portal"
                                                customInput={
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                cursor: 'pointer',
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                }
                                                calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                            />
                                        </TableCell>
                                    }

                                    {/* Начало строительства - Факт */}
                                    {(Array.isArray(formData.fact_start) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.fact_start) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                            p: 0.5,
                                        }}>
                                            <DatePicker
                                                selected={Array.isArray(formData.fact_start) ? (formData.fact_start[rowIndex] ? new Date(formData.fact_start[rowIndex]) : null) : (formData.fact_start ? new Date(formData.fact_start) : null)}
                                                onChange={(date) => {
                                                    const formattedDate = date ?
                                                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                        '';

                                                    if (Array.isArray(formData.fact_start)) {
                                                        let newValue = [...formData.fact_start]
                                                        newValue[rowIndex] = formattedDate
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'fact_start': newValue
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'fact_start': formattedDate
                                                        }));
                                                    }
                                                }}
                                                locale="ru"
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="ДД.ММ.ГГГГ"
                                                isClearable
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                yearDropdownItemNumber={10}
                                                scrollableYearDropdown
                                                portalId="root-portal"
                                                customInput={
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                cursor: 'pointer',
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                }
                                                calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                            />
                                        </TableCell>
                                    }

                                    {/* Завершение строительства - План */}
                                    {(Array.isArray(formData.plan_end) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.plan_end) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                            p: 0.5,
                                        }}>
                                            <DatePicker
                                                selected={Array.isArray(formData.plan_end) ? (formData.plan_end[rowIndex] ? new Date(formData.plan_end[rowIndex]) : null) : (formData.plan_end ? new Date(formData.plan_end) : null)}
                                                onChange={(date) => {
                                                    const formattedDate = date ?
                                                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                        '';

                                                    if (Array.isArray(formData.plan_end)) {
                                                        let newValue = [...formData.plan_end]
                                                        newValue[rowIndex] = formattedDate
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'plan_end': newValue
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'plan_end': formattedDate
                                                        }));
                                                    }
                                                }}
                                                locale="ru"
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="ДД.ММ.ГГГГ"
                                                isClearable
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                yearDropdownItemNumber={10}
                                                scrollableYearDropdown
                                                portalId="root-portal"
                                                customInput={
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                cursor: 'pointer',
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                }
                                                calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                            />
                                        </TableCell>
                                    }

                                    {/* Завершение строительства - Факт */}
                                    {(Array.isArray(formData.fact_end) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.fact_end) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                            p: 0.5,
                                        }}>
                                            <DatePicker
                                                selected={Array.isArray(formData.fact_end) ? (formData.fact_end[rowIndex] ? new Date(formData.fact_end[rowIndex]) : null) : (formData.fact_end ? new Date(formData.fact_end) : null)}
                                                onChange={(date) => {
                                                    const formattedDate = date ?
                                                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                        '';

                                                    if (Array.isArray(formData.fact_end)) {
                                                        let newValue = [...formData.fact_end]
                                                        newValue[rowIndex] = formattedDate
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'fact_end': newValue
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            'fact_end': formattedDate
                                                        }));
                                                    }
                                                }}
                                                locale="ru"
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="ДД.ММ.ГГГГ"
                                                isClearable
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                yearDropdownItemNumber={10}
                                                scrollableYearDropdown
                                                portalId="root-portal"
                                                customInput={
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                cursor: 'pointer',
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                }
                                                calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                            />
                                        </TableCell>
                                    }



                                    {/* Этап */}
                                    {(Array.isArray(formData.stage) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.stage) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                            p: 0.5,
                                        }}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                size="small"
                                                value={Array.isArray(formData.stage) ? formData.stage[rowIndex] : formData.stage}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || /^\d+$/.test(value)) {
                                                        if (Array.isArray(formData.stage)) {
                                                            let newValue = [...formData.stage]
                                                            newValue[rowIndex] = value ? parseInt(value, 10) : null
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                stage: newValue
                                                            }));
                                                        } else {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                stage: value ? parseInt(value, 10) : null
                                                            }));
                                                        }

                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '0.75rem',
                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? fieldColors.closing.dark : fieldColors.closing.light,
                                                            borderWidth: '2px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        p: 0.5,
                                                        color: darkMode ? '#fff' : '#000',
                                                    },
                                                }}
                                            />
                                        </TableCell>

                                    }


                                    {/* Закрытие */}
                                    <TableCell sx={{
                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                        p: 0.5,
                                    }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            size="small"
                                            value={formData.closing[rowIndex] || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(',', '.');
                                                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                    let newValue = [...formData.closing]
                                                    newValue[rowIndex] = value ? parseFloat(value) : null
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        closing: newValue
                                                    }));
                                                }
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    fontSize: '0.75rem',
                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    p: 0.5,
                                                    color: darkMode ? '#fff' : '#000',
                                                },
                                            }}
                                        />
                                    </TableCell>

                                    {/* Стоимость */}
                                    <TableCell sx={{
                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                        p: 0.5,
                                    }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            size="small"
                                            value={formData.cost[rowIndex] || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(',', '.');
                                                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                    let newValue = [...formData.cost]
                                                    newValue[rowIndex] = value ? parseFloat(value) : null
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        cost: newValue
                                                    }));
                                                }
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    fontSize: '0.75rem',
                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    p: 0.5,
                                                    color: darkMode ? '#fff' : '#000',
                                                },
                                            }}
                                        />
                                    </TableCell>

                                    {/* Комментарий */}
                                    <TableCell sx={{
                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                        p: 0.5,
                                    }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            size="small"
                                            value={formData.comment[rowIndex] || ''}
                                            onChange={(e) => {
                                                let newValue = [...formData.cost]
                                                newValue[rowIndex] = e.target.value
                                                setFormData(prev => ({
                                                    ...prev,
                                                    comment: newValue
                                                }));
                                            }}
                                            placeholder="Markdown..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    fontSize: '0.75rem',
                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    p: 0.5,
                                                    color: darkMode ? '#fff' : '#000',
                                                },
                                            }}
                                        />
                                    </TableCell>

                                    {/* Ответственный */}
                                    {(Array.isArray(formData.responsible) || rowIndex === 0) &&
                                        <TableCell rowSpan={Array.isArray(formData.responsible) ? undefined : equipmentRows.length} sx={{
                                            borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                            p: 0.5,
                                        }}>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={responsible}
                                                getOptionLabel={(option) => option.name}
                                                value={Array.isArray(formData.responsible) ? formData.responsible[rowIndex] : formData.responsible || null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                onChange={(event, value) => {
                                                    if (Array.isArray(formData.responsible)) {
                                                        let newValue = [...formData.responsible]
                                                        newValue[rowIndex] = value
                                                        setFormData(prev => {
                                                            let updatedCoexecutors = prev.coexecutors
                                                            if (updatedCoexecutors && updatedCoexecutors[0] && Array.isArray(updatedCoexecutors[0])) {
                                                                updatedCoexecutors[rowIndex] = updatedCoexecutors[rowIndex]?.filter(coexecutor =>
                                                                    coexecutor.id !== value?.id
                                                                ) || [];
                                                            } else {
                                                                updatedCoexecutors = updatedCoexecutors?.filter(coexecutor =>
                                                                    coexecutor.id !== value?.id
                                                                ) || [];
                                                            }

                                                            return {
                                                                ...prev,
                                                                responsible: newValue,
                                                                coexecutors: updatedCoexecutors
                                                            };
                                                        });
                                                    } else {
                                                        setFormData(prev => {
                                                            const updatedCoexecutors = prev.coexecutors?.filter(coexecutor =>
                                                                coexecutor.id !== value?.id
                                                            ) || [];
                                                            return {
                                                                ...prev,
                                                                responsible: value,
                                                                coexecutors: updatedCoexecutors
                                                            };
                                                        });
                                                    }

                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.responsible.dark : fieldColors.responsible.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                slotProps={{
                                                    popper: {
                                                        sx: {
                                                            zIndex: 13000,
                                                            '& .MuiAutocomplete-paper': {
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    }



                                    {/* Соисполнители */}
                                    {((formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0])) || rowIndex === 0) &&
                                        <TableCell rowSpan={formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0]) ? undefined : equipmentRows.length} sx={{ p: 0.5 }}>
                                            <Autocomplete
                                                fullWidth
                                                multiple
                                                size="small"
                                                disableCloseOnSelect
                                                disabled={!formData.responsible}
                                                options={responsible.filter(option => option.id !== formData.responsible?.id)}
                                                getOptionLabel={(option) => option.name}
                                                value={formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0]) ? formData.coexecutors[rowIndex] : formData.coexecutors || []}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                onChange={(event, value) => {
                                                    if (formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0])) {
                                                        let newValue = [...formData.coexecutors]
                                                        newValue[rowIndex] = value
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            coexecutors: newValue
                                                        }));
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            coexecutors: value
                                                        }));
                                                    }

                                                }}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            {...getTagProps({ index })}
                                                            key={option.id}
                                                            label={option.name}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                                                color: darkMode ? '#ffb74d' : '#f57c00',
                                                                fontSize: '0.65rem',
                                                                height: 'auto',
                                                                minHeight: '20px',
                                                                '& .MuiChip-label': {
                                                                    whiteSpace: 'normal',
                                                                    wordBreak: 'break-word',
                                                                    textAlign: 'center',
                                                                    lineHeight: '1.2',
                                                                    padding: '2px 6px',
                                                                }
                                                            }}

                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.coexecutors.dark : fieldColors.coexecutors.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                slotProps={{
                                                    popper: {
                                                        sx: {
                                                            zIndex: 13000,
                                                            '& .MuiAutocomplete-paper': {
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    }


                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box >
    );
};

const ObjectDialog = ({
    open,
    currentUser,
    onClose,
    objectData = null,
    darkMode = false,
    responsible = [],
    equipment = [],
    stages = [],
    projects = [],
    selectedProjectId = null
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isEditing = !!objectData;
    const transformers = useSelector(selectTransformers)




    const [formData, setFormData] = useState({
        name: null,
        project_id: null,
        plan_start: null,
        fact_start: null,
        plan_end: null,
        fact_end: null,
        stage: null,
        responsible: null,
        coexecutors: [],
        equipment: [],
        closing: [],
        cost: [],
        comment: [],
        creator: null,
        modifier: null,
    });

    // Состояние для хранения проектов, где уже есть данный объект
    const [projectsWithObject, setProjectsWithObject] = useState([]);
    const [objectsIntProject, setObjectsIntProject] = useState([]);
    const [isCheckingProjects, setIsCheckingProjects] = useState(false);

    const objects = useMemo(() => {
        return transformers.filter(ktp => !objectsIntProject.includes(ktp.fid))
    }, [transformers, objectsIntProject])

    // Функция для проверки существования объекта в проектах
    const checkObjectInProjects = useCallback(async (objectId) => {
        if (!objectId) return;

        setIsCheckingProjects(true);
        try {
            const response = await $authHost.get('/apig/reestr', {
                params: {
                    object_id: objectId,
                    object_type: 'transformers',
                    is_active: 'true'
                }
            });

            if (response.data.success) {
                const existingProjects = response.data.data
                    .filter(obj => obj.project_id && obj.id !== (objectData?.id || null))
                    .map(obj => obj.project_id);
                setProjectsWithObject(existingProjects);
            }
        } catch (error) {
            console.error('Error checking object in projects:', error);
            setProjectsWithObject([]);
        } finally {
            setIsCheckingProjects(false);
        }
    }, [objectData?.id]);

    // Функция для проверки существования объекта в проектах
    const getObjecstInProject = useCallback(async (projectId) => {
        if (!projectId) return;

        setIsCheckingProjects(true);
        try {
            const response = await $authHost.get('/apig/reestr', {
                params: {
                    project_id: projectId,
                    object_type: 'transformers',
                    is_active: 'true'
                }
            });

            if (response.data.success) {
                const objectsInProjects = response.data.data
                    .filter(obj => obj.object_id)
                    .map(obj => obj.object_id);
                setObjectsIntProject(objectsInProjects);
            }
        } catch (error) {
            console.error('Error checking object in projects:', error);
            setObjectsIntProject([]);
        } finally {
            setIsCheckingProjects(false);
        }
    }, [objectData?.id]);

    // Проверяем объект в проектах при изменении объекта
    useEffect(() => {
        console.log(formData)
        if (formData.object?.fid) {
            checkObjectInProjects(formData.object.fid);
        } else {
            setProjectsWithObject([]);
        }
    }, [formData.object, checkObjectInProjects]);

    useEffect(() => {
        console.log(formData)
        if (formData.project?.id) {
            getObjecstInProject(formData.project.id);
        } else {
            setObjectsIntProject([]);
        }
    }, [formData.project?.id, getObjecstInProject]);

    // Получаем информацию о заблокированных проектах (исключая текущий проект при редактировании)
    const blockedProjects = (projects || []).filter(project =>
        projectsWithObject.includes(project.id) &&
        !(isEditing && objectData?.project_id === project.id)
    );

    // Создаем список проектов с заблокированными вверху
    const sortedProjects = [
        ...blockedProjects, // заблокированные проекты вверху
        ...(projects || []).filter(project =>
            !projectsWithObject.includes(project.id) ||
            (isEditing && objectData?.project_id === project.id)
        ) // доступные проекты + текущий проект при редактировании
    ];

    console.log(projectsWithObject)

    // Заполняем форму данными объекта при редактировании
    useEffect(() => {
        if (isEditing && objectData && open) {
            console.log(objectData)
            // Преобразуем строковые массивы обратно в объекты для responsible
            // const responsibleObjects = objectData.responsible ?
            //     objectData.responsible.map(name =>
            //         responsible.find(r => r.name === name) || { id: Math.random(), name, role: 'Неизвестно' }
            //     ) : [];
            const resp = Array.isArray(objectData?.responsible?.v) ? objectData?.responsible?.v.map(v => responsible.find(r => r.id === v)) : responsible.find(r => r.id === objectData?.responsible?.v)
            const eq = Array.isArray(objectData?.equipment?.v) ? objectData?.equipment?.v.map(v => equipment.find(r => Number(r.id) === Number(v))) : equipment.find(r => Number(r.id) === Number(objectData?.equipment?.v))
            const coexecutors = objectData.coexecutors?.v && Array.isArray(objectData?.coexecutors?.v[0])
                ? objectData?.coexecutors?.v.map(v => v.map(c => responsible.find(r => r.id === c)))
                : objectData?.coexecutors?.v.map(v => responsible.find(r => r.id === v))

            setFormData({
                object: objectData.object || null,
                project: projects.find(p => p.id === objectData.project_id) || projects.find(p => p.id === selectedProjectId) || null,
                plan_start: objectData.plan_start.v || null,
                fact_start: objectData.fact_start.v || null,
                plan_end: objectData.plan_end.v || null,
                fact_end: objectData.fact_end.v || null,
                stage: objectData.stage.v || null,
                responsible: resp || null,
                coexecutors: coexecutors || [],
                equipment: eq || [],
                closing: objectData.closing.v || [],
                cost: objectData.cost.v || [],
                comment: objectData.comment.v || [],
                creator: objectData.creator || 'Неизвестно',
                modifier: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
            });
        } else if (!isEditing && open) {
            // Очищаем форму для создания нового объекта
            setFormData({
                object: null,
                project: projects.find(p => p.id === selectedProjectId) || null,
                plan_start: null,
                fact_start: null,
                plan_end: null,
                fact_end: null,
                stage: null,
                responsible: null,
                coexecutors: [],
                equipment: [],
                closing: [],
                cost: [],
                comment: [],
                creator: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
                modifier: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
            });
        }
    }, [objectData, open, isEditing, selectedProjectId]);

    // Устанавливаем проект по умолчанию при открытии диалога
    // useEffect(() => {
    //     if (open && !isEditing && selectedProjectId && projects.length > 0) {
    //         const defaultProject = projects.find(p => p.id === selectedProjectId);
    //         if (defaultProject) {
    //             setFormData(prev => ({
    //                 ...prev,
    //                 project: defaultProject
    //             }));
    //         }
    //     }
    // }, [open, isEditing, selectedProjectId, projects]);

    // Сбрасываем проект, если он стал недоступным (но не при редактировании текущего проекта)
    useEffect(() => {
        if (formData.project && blockedProjects.some(p => p.id === formData.project.id)) {
            setFormData(prev => ({
                ...prev,
                project: null
            }));
        }
    }, [blockedProjects, formData.project]);



    const handleSave = async () => {
        // Проверяем, что выбранный проект не заблокирован
        if (formData.project && blockedProjects.some(p => p.id === formData.project.id)) {
            alert('Выбранный проект недоступен для данного объекта. Пожалуйста, выберите другой проект.');
            return;
        }

        // Валидация дат


        const objectToSave = {
            ...formData,
            object_id: formData.object.fid || formData.object.id,
            object_type: formData.object.type,
            project_id: formData.project?.id || null,
            plan_start: { v: formData.plan_start || undefined },
            plan_end: { v: formData.plan_end || undefined },
            fact_start: { v: formData.fact_start || undefined },
            fact_end: { v: formData.fact_end || undefined },
            equipment: { v: formData.equipment.map(e => e.id) || [] },
            responsible: { v: Array.isArray(formData.responsible) ? formData.responsible.map(r => r.id) : formData.responsible?.id || null },
            coexecutors: {
                v: formData.coexecutors && formData.coexecutors[0] && Array.isArray(formData.coexecutors[0])
                    ? formData.coexecutors.map(c => c.map(r => r.id))
                    : formData.coexecutors.map(c => c.id)
            },
            closing: { v: formData.closing || [Array.from({ length: formData.equipment.length }, () => null)] },
            cost: { v: formData.cost || [Array.from({ length: formData.equipment.length }, () => null)] },
            comment: { v: formData.comment || [Array.from({ length: formData.equipment.length }, () => null)] },
            id: isEditing ? objectData.id : Date.now(),
            creator: formData.creator.id,
            modifier: formData.modifier.id
        };

        console.log('Saving object:', objectToSave);

        try {
            let res;
            if (isEditing) {
                res = await $authHost.put(`/apig/reestr/${objectData.id}`, objectToSave);
            } else {
                res = await $authHost.post(`/apig/reestr`, objectToSave);
            }

            console.log(isEditing ? 'Updating object:' : 'Creating object:', objectToSave);

            if (res?.data?.success) {
                onClose(true);
            } else {
                alert('Ошибка при сохранении: ' + (res?.data?.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            console.error('Error saving object:', error);
            alert('Ошибка при сохранении: ' + (error.message || 'Неизвестная ошибка'));
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const dialogTitle = isEditing ? 'Редактирование объекта' : 'Создание нового объекта';
    const buttonText = isEditing ? 'Сохранить изменения' : 'Создать объект';
    const buttonColor = isEditing ? '#1976d2' : '#7cb342';
    const IconComponent = isEditing ? EditIcon : AddIcon;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 5100 }}
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderRadius: isMobile ? 0 : 3,
                        boxShadow: darkMode
                            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                            : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        animation: 'slideIn 0.3s ease-out',
                        position: 'relative',
                        zIndex: 1301,
                        '@keyframes slideIn': {
                            from: {
                                opacity: 0,
                                transform: 'scale(0.95)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'scale(1)',
                            },
                        },
                    }
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1,
                px: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: `${buttonColor}20`,
                        border: `2px solid ${buttonColor}`
                    }}>
                        <IconComponent sx={{
                            fontSize: '24px',
                            color: buttonColor
                        }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            mb: 0.3,
                            color: darkMode ? '#f5f7fa' : '#333'
                        }}>
                            {dialogTitle}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={handleCancel}
                    size="large"
                    sx={{
                        color: darkMode ? '#f5f7fa' : '#666',
                        '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                p: 3,
                position: 'relative',
                zIndex: 1302
            }}>
                <ObjectForm
                    formData={formData}
                    darkMode={darkMode}
                    isEditing={isEditing}
                    setFormData={setFormData}
                    objects={objects}
                    stages={stages}
                    responsible={responsible}
                    equipment={equipment}
                    projects={projects}
                    selectedProjectId={selectedProjectId}
                    sortedProjects={sortedProjects}
                    blockedProjects={blockedProjects}
                    isCheckingProjects={isCheckingProjects}
                />
            </DialogContent>

            <DialogActions sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderTop: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1.5,
                px: 3,
                gap: 2,
                justifyContent: 'flex-end'
            }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    size="large"
                    sx={{
                        color: darkMode ? '#bbb' : '#666',
                        borderColor: darkMode ? '#555' : '#ddd',
                        px: 1,
                        py: 0.3,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        '&:hover': {
                            borderColor: darkMode ? '#777' : '#bbb',
                            bgcolor: 'transparent'
                        }
                    }}
                >
                    Отмена
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={!formData.object || !formData.project || blockedProjects.some(p => p.id === formData.project?.id)}
                    sx={{
                        bgcolor: buttonColor,
                        color: 'white',
                        px: 1,
                        py: 0.3,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        '&:hover': {
                            bgcolor: isEditing ? '#1565c0' : '#689f38',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${buttonColor}66`
                        },
                        '&:disabled': {
                            bgcolor: darkMode ? '#444' : '#ddd',
                            color: darkMode ? '#666' : '#999'
                        }
                    }}
                >
                    {buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ObjectDialog; 